import { useEffect, useState } from 'react'
import { fetchDebrief, notifyDebrief, saveDebrief, type DebriefView } from '../data/api'
import { SectionLabel } from './RoundTimeline'
import { useCanCoach } from '../data/session'

// The post-match path in one place: read the review, mark the rounds, put someone on each, tell
// them. All four were already possible on four different pages, and the last one — the one that
// makes the other three worth doing — is the one that got skipped.
//
// A checklist, not a wizard. Nothing here disables anything: a coach who marks rounds before
// reading the review gets a list that agrees with them. `next` only decides what to point at.

const card = { borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' } as const

const NOTIFY_ERR: Record<string, string> = {
  no_rounds: 'Mark a round or two first — an empty debrief has nothing to send.',
  no_webhook: 'No Discord webhook is configured on the API, so this can’t post.',
  post_failed: 'Discord refused the post. Nothing was marked as sent — try again.',
  no_debrief: 'Nothing recorded yet.',
}

export function Debrief({ demoId, rounds, onRound }: { demoId: string; rounds: number; onRound?: (r: number) => void }) {
  const canCoach = useCanCoach()
  const [view, setView] = useState<DebriefView | null>(null)
  const [pick, setPick] = useState('')
  const [why, setWhy] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    fetchDebrief(demoId).then(setView).catch(() => setView(null))
  }, [demoId])

  if (!view) return null
  const d = view.debrief
  const picked = d?.rounds ?? []

  const patch = async (p: Parameters<typeof saveDebrief>[1]) => {
    setBusy(true)
    setErr(null)
    const next = await saveDebrief(demoId, p)
    setBusy(false)
    if (next) setView(next)
    else setErr('Couldn’t save that.')
  }

  const addRound = async () => {
    const n = Number(pick)
    if (!Number.isFinite(n) || n < 1 || n > rounds) return
    if (picked.some((r) => r.round === n)) return
    await patch({ rounds: [...picked, { round: n, ...(why.trim() ? { why: why.trim() } : {}) }] })
    setPick('')
    setWhy('')
  }

  const send = async () => {
    setBusy(true)
    setErr(null)
    const res = await notifyDebrief(demoId)
    setBusy(false)
    if (!res.ok) {
      setErr(NOTIFY_ERR[res.error] ?? res.error)
      return
    }
    setView(await fetchDebrief(demoId))
  }

  return (
    <section className="border border-border bg-surface p-5" style={card}>
      <SectionLabel n="D1" label="Debrief" />
      <p className="mt-2 text-xs text-text-faint">
        Four steps, in the order they’re usually done — not the order they have to be. Nothing here
        stops you working another way.
      </p>

      <ol className="mt-4 space-y-2">
        {view.steps.map((s) => (
          <li key={s.id} className="flex items-baseline gap-2 text-sm">
            <span aria-hidden style={{ color: s.done ? 'var(--accent)' : 'var(--text-faint)' }}>
              {s.done ? '✓' : '○'}
            </span>
            <span className={s.done ? 'text-text-dim line-through' : 'text-text'}>{s.label}</span>
            {view.next === s.id && <span className="text-[10px] uppercase tracking-wider text-accent">next</span>}
            <span className="text-[11px] text-text-faint">{s.hint}</span>
          </li>
        ))}
      </ol>

      {err && (
        <p role="alert" className="mt-3 text-xs text-loss">
          {err}
        </p>
      )}

      {canCoach && (
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          <label className="flex items-center gap-2 text-xs text-text-dim">
            <input
              type="checkbox"
              checked={!!d?.reviewed}
              onChange={(e) => patch({ reviewed: e.target.checked })}
            />
            I’ve read the AI review
          </label>

          <div className="flex flex-wrap items-end gap-2">
            <input
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              placeholder="round"
              inputMode="numeric"
              aria-label="Round number to mark"
              className="w-20 border border-border bg-bg-2 px-2 py-1 text-xs text-text outline-none"
              style={{ borderRadius: 'var(--radius)' }}
            />
            <input
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addRound()}
              placeholder="why it’s worth watching (optional)"
              maxLength={200}
              aria-label="Why this round matters"
              className="min-w-56 flex-1 border border-border bg-bg-2 px-2 py-1 text-xs text-text outline-none"
              style={{ borderRadius: 'var(--radius)' }}
            />
            <button
              onClick={addRound}
              disabled={busy || !pick}
              className="border border-border-bright px-3 py-1 text-xs text-accent disabled:opacity-40"
              style={{ borderRadius: 'var(--radius)' }}
            >
              mark
            </button>
          </div>

          {picked.length > 0 && (
            <ul className="space-y-1">
              {picked.map((r) => (
                <li key={r.round} className="flex items-baseline gap-2 text-xs">
                  <button onClick={() => onRound?.(r.round)} className="text-accent hover:underline">
                    round {r.round}
                  </button>
                  <span className="text-text-faint">{r.why}</span>
                  <button
                    onClick={() => patch({ rounds: picked.filter((x) => x.round !== r.round) })}
                    aria-label={`Unmark round ${r.round}`}
                    className="ml-auto text-text-faint hover:text-loss"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Not disabled when the earlier steps are unfinished — it's a checklist. Only an empty
              round list makes this meaningless, and the API says so rather than the button hiding. */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={send}
              disabled={busy}
              className="font-display px-4 py-2 text-xs font-semibold uppercase tracking-wider disabled:opacity-50"
              style={{ borderRadius: 'var(--radius)', background: 'var(--accent)', color: 'var(--accent-ink)' }}
            >
              {d?.notifiedAt ? 'send again' : 'send to Discord'}
            </button>
            <span className="text-[11px] text-text-faint">
              {picked.length > 5
                ? `${picked.length} rounds is a lot — a debrief nobody finishes is one nobody watches.`
                : 'Posts the marked rounds with a link back to this match.'}
            </span>
          </div>
        </div>
      )}
    </section>
  )
}
